import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import snapshot from '../data/pricing-snapshot.json';

// New API 计价约定：model_ratio 1 = $2 / 1M tokens
const PRICE_PER_RATIO = 2;

const VENDOR_GLYPH = {
  anthropic: ["▘ ▝", " ▟ ", "▖ ▗"],
  openai:    [" ◜◝ ", "◜  ◝", " ◟◞ "],
  default:   ["╭─╮", "│·│", "╰─╯"],
};

function tagFor(m) {
  const n = m.model_name;
  if (m.quota_type === 1) return "IMAGE";
  if (n.includes("codex")) return "CODE";
  if (n.includes("mini") || n.includes("haiku")) return "FAST";
  if (n.includes("opus")) return "FLAGSHIP";
  if (m.model_ratio >= 2.5) return "FLAGSHIP";
  return "BALANCED";
}

function fmtUsd(v) {
  if (v >= 100) return "$" + Math.round(v);
  if (v >= 10) return "$" + parseFloat(v.toFixed(1));
  if (v >= 0.1) return "$" + parseFloat(v.toFixed(2));
  return "$" + parseFloat(v.toFixed(3));
}

// Claude 按 opus → sonnet → haiku 排，同级及其他厂商按名称倒序
function tierOf(name) {
  if (name.includes("opus")) return 0;
  if (name.includes("sonnet")) return 1;
  if (name.includes("haiku")) return 2;
  return 3;
}

// 按厂商把模型分块，厂商顺序跟随接口 vendors 列表
function buildBlocks(pricing) {
  const vendorById = {};
  (pricing.vendors || []).forEach(v => { vendorById[v.id] = v.name; });
  const blocks = new Map();
  (pricing.vendors || []).forEach(v => {
    blocks.set(v.name, { vendor: v.name.toLowerCase(), family: v.name, items: [] });
  });
  (pricing.data || []).forEach(m => {
    const name = vendorById[m.vendor_id] || "Other";
    if (!blocks.has(name)) blocks.set(name, { vendor: name.toLowerCase(), family: name, items: [] });
    blocks.get(name).items.push(m);
  });
  const list = [...blocks.values()].filter(b => b.items.length > 0);
  list.forEach(b => b.items.sort((a, c) =>
    (tierOf(a.model_name) - tierOf(c.model_name)) || c.model_name.localeCompare(a.model_name)
  ));
  return list;
}

function StatusDot({ kind = "ok" }) {
  return <span className={`dot dot-${kind}`} aria-hidden="true" />;
}

// 展示原价（官方定价），不乘分组倍率
function ModelCard({ m }) {
  const tag = tagFor(m);
  const perCall = m.quota_type === 1;
  const baseInput = m.model_ratio * PRICE_PER_RATIO;
  const baseOutput = baseInput * (m.completion_ratio || 1);

  return (
    <div className="model-card">
      <div className="mc-head">
        <span className="mc-id">{m.model_name}</span>
        <span className={`mc-tag tag-${tag.toLowerCase().replace("-", "")}`}>{tag}</span>
      </div>
      <div className="mc-body">
        {perCall ? (
          <div className="mc-row"><span className="mc-k">PRICE</span><span className="mc-v">{fmtUsd(m.model_price)} / image</span></div>
        ) : (
          <>
            <div className="mc-row"><span className="mc-k">IN   </span><span className="mc-v">{fmtUsd(baseInput)} / 1M tokens</span></div>
            <div className="mc-row"><span className="mc-k">OUT  </span><span className="mc-v">{fmtUsd(baseOutput)} / 1M tokens</span></div>
          </>
        )}
        <div className="mc-row"><span className="mc-k">GROUP</span><span className="mc-v">{(m.enable_groups || []).join(" · ")}</span></div>
      </div>
      <div className="mc-foot">
        <span className="mc-status"><StatusDot /> ONLINE</span>
        <span className="mc-call">$ curl ... -d '{`{"model":"${m.model_name}"}`}'</span>
      </div>
    </div>
  );
}

function VendorBlock({ group }) {
  const { t } = useLanguage();
  const total = group.items.length;
  return (
    <div className="vendor-block">
      <div className="vb-head">
        <pre className="vb-glyph" aria-hidden="true">{(VENDOR_GLYPH[group.vendor] || VENDOR_GLYPH.default).join("\n")}</pre>
        <div className="vb-title">
          <div className="vb-family">{group.family}</div>
          <div className="vb-vendor">{t('models.vendor')}<b>{group.vendor}</b> · {total}{t('models.count')}</div>
        </div>
        <div className="vb-bar">
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className={"vb-cell " + (i < total * 3 ? "on" : "")} />
          ))}
        </div>
      </div>
      <div className="vb-grid">
        {group.items.map(m => <ModelCard key={m.model_name} m={m} />)}
      </div>
    </div>
  );
}

function SectionHead({ kicker, title, sub }) {
  return (
    <header className="section-head">
      <div className="sh-row">
        <span className="sh-rule" />
        <span className="sh-kicker">{kicker}</span>
      </div>
      <h2 className="sh-title">{title}</h2>
      {sub && <p className="sh-sub">{sub}</p>}
    </header>
  );
}

function Models() {
  const { t } = useLanguage();
  // 先用打包快照渲染，再异步换成 /api/models 的实时数据
  const [pricing, setPricing] = useState(snapshot);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    let alive = true;
    fetch('/api/models')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then(json => {
        if (alive && json && json.success === true && Array.isArray(json.data)) setPricing(json);
      })
      .catch(() => { /* 拉取失败时继续用快照数据 */ });
    return () => { alive = false; };
  }, []);

  const groupRatio = useMemo(() => pricing.group_ratio || {}, [pricing]);
  const groupNames = useMemo(() => Object.keys(groupRatio), [groupRatio]);
  const active = filter !== "ALL" && groupNames.includes(filter) ? filter : "ALL";

  const blocks = useMemo(() => buildBlocks(pricing), [pricing]);
  const filtered = useMemo(() => {
    if (active === "ALL") return blocks;
    return blocks
      .map(g => ({ ...g, items: g.items.filter(m => (m.enable_groups || []).includes(active)) }))
      .filter(g => g.items.length > 0);
  }, [blocks, active]);

  const totalCount = blocks.reduce((a, g) => a + g.items.length, 0);
  const groupNote = active !== "ALL" && pricing.usable_group ? pricing.usable_group[active] : null;

  return (
    <section id="models" className="section section-models">
      <SectionHead
        kicker={t('models.kicker')}
        title={t('models.title')}
        sub={t('models.subtitle', { vendors: blocks.length, total: totalCount })}
      />
      <div className="filterbar">
        <span className="fb-k">{t('models.filter')}</span>
        {["ALL", ...groupNames].map(name => (
          <button
            key={name}
            className={"fb-chip " + (active === name ? "on" : "")}
            onClick={() => setFilter(name)}
          >
            {name.toLowerCase()}
          </button>
        ))}
        <span className="fb-count">{t('models.matched')}{filtered.reduce((a, g) => a + g.items.length, 0)}</span>
      </div>
      {groupNote && <p className="fb-note">{'// '}{groupNote}</p>}
      <div className="vendor-list">
        {filtered.map(g => <VendorBlock key={g.vendor} group={g} />)}
      </div>
    </section>
  );
}

export default Models;
